import type { ReactNode } from "react"
import { Link } from "react-router"
import {
  Button,
  Card,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@alascorporation/ui"
import { RefreshIcon } from "../../icons"
import { hasCurrentPolicy, type PermissionKey } from "../../lib/auth"

function FilterIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 4h14M6 10h8M9 16h2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  )
}

function ColumnsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="4" width="14" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8.3 4v12M12.7 4v12" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )
}

function ImportIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10 3v9m0 0l-3.5-3.5M10 12l3.5-3.5M4 14.5v.5A2 2 0 006 17h8a2 2 0 002-2v-.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

type ModuleTab = {
  label: string
  to: string
  active?: boolean
}

export type ModuleMetricTone = "neutral" | "good" | "warning" | "danger"

export type ModuleMetric = {
  label: string
  value: string
  /** Qué significa este número para quien decide: por qué importa o qué hacer si sube/baja. */
  hint?: string
  /** Colorea el valor (y, para warning/danger, toda la tarjeta) para que el estado se lea sin pensar. */
  tone?: ModuleMetricTone
  trend?: string
  /** Ícono opcional mostrado en un cuadro de color junto al label. */
  icon?: ReactNode
  /** Si se da, la tarjeta enlaza a la vista ya filtrada que explica el número. */
  to?: string
}

export type ModuleFilter =
  | {
      type: "select"
      key: string
      label: string
      value: string
      onChange: (value: string) => void
      options: { label: string; value: string }[]
    }
  | {
      type: "checkbox"
      key: string
      label: string
      checked: boolean
      onChange: (checked: boolean) => void
    }
  | {
      type: "text"
      key: string
      label: string
      value: string
      onChange: (value: string) => void
      placeholder?: string
    }

export type ModuleToolbar = {
  search?: {
    value: string
    onChange: (value: string) => void
    placeholder?: string
  }
  filters?: ModuleFilter[]
  /** Columnas que el usuario puede mostrar/ocultar — omitir si la tabla no soporta esto todavía. */
  columns?: { key: string; label: string }[]
  visibleColumnKeys?: string[]
  onVisibleColumnKeysChange?: (keys: string[]) => void
  /** Recarga los datos de la tabla (ej. reload() de useServerTable) sin recargar la página. */
  onRefresh?: () => void
  /** Si es true, deshabilita el botón de actualizar y anima su ícono mientras carga. */
  refreshing?: boolean
  onImport?: () => void
  onCreate?: () => void
  createLabel?: string
  /** Policy key(s) required to see "Importar"/"+ Nuevo…" — the page's `:edit` policy. Omit only when the page has no dedicated edit policy. */
  requires?: PermissionKey | PermissionKey[]
  /** Escape hatch para controles que el contrato estructurado todavía no cubre. Evitar cuando sea posible. */
  extra?: ReactNode
}

/**
 * La única barra de herramientas (buscar/filtros/actualizar/importar/crear) para
 * cualquier listado con DataTable — usada dentro de ModuleLayout y también sola
 * en vistas embebidas (tabs de detalle) para que ambas se vean y midan igual.
 * No reimplementar este markup en otro lugar.
 */
export function ModuleToolbar({ toolbar }: { toolbar: ModuleToolbar }) {
  const canMutate = !toolbar.requires || hasCurrentPolicy(...(Array.isArray(toolbar.requires) ? toolbar.requires : [toolbar.requires]))
  return (
    <Card className="flex flex-col gap-3 rounded-2xl border-gray-200 bg-white p-4 sm:flex-row sm:flex-wrap sm:items-center dark:border-gray-800 dark:bg-white/[0.03]">
      {toolbar.search ? (
        <input
          type="text"
          aria-label={toolbar.search.placeholder ?? "Buscar"}
          data-tour="module-search"
          value={toolbar.search.value}
          onChange={(e) => toolbar.search!.onChange(e.target.value)}
          placeholder={toolbar.search.placeholder ?? "Buscar..."}
          className="h-11 w-full min-w-0 rounded-lg border border-gray-300 px-3 text-sm sm:h-10 sm:w-auto sm:flex-1 sm:min-w-[240px] dark:border-gray-700 dark:bg-gray-900"
        />
      ) : null}

      {toolbar.filters && toolbar.filters.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              aria-label="Filtros"
              title="Filtros"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg sm:h-10 sm:w-10"
            >
              <FilterIcon className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64 space-y-3 p-3">
            <DropdownMenuLabel className="px-0 pb-0 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Filtros
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {toolbar.filters.map((filter) => (
              <div key={filter.key} className="space-y-1">
                {filter.type === "select" || filter.type === "text" ? (
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                    {filter.label}
                  </label>
                ) : null}
                <ModuleFilterControl filter={filter} />
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      {toolbar.columns && toolbar.columns.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              aria-label="Columnas"
              title="Columnas"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg sm:h-10 sm:w-10"
            >
              <ColumnsIcon className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Mostrar columnas
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {toolbar.columns.map((column) => {
              const visible = !toolbar.visibleColumnKeys || toolbar.visibleColumnKeys.includes(column.key)
              return (
                <DropdownMenuCheckboxItem
                  key={column.key}
                  checked={visible}
                  onCheckedChange={(checked) => {
                    if (!toolbar.onVisibleColumnKeysChange) return
                    const current = toolbar.visibleColumnKeys ?? toolbar.columns!.map((c) => c.key)
                    toolbar.onVisibleColumnKeysChange(
                      checked ? [...current, column.key] : current.filter((k) => k !== column.key),
                    )
                  }}
                  onSelect={(e) => e.preventDefault()}
                >
                  {column.label}
                </DropdownMenuCheckboxItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      {toolbar.extra}

      <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
        {toolbar.onRefresh ? (
          <Button
            type="button"
            variant="outline"
            onClick={toolbar.onRefresh}
            disabled={toolbar.refreshing}
            aria-label="Actualizar"
            title="Actualizar"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg sm:h-10 sm:w-10"
          >
            <RefreshIcon className={`size-4 ${toolbar.refreshing ? "animate-spin" : ""}`} />
          </Button>
        ) : null}
        {toolbar.onImport && canMutate ? (
          <Button
            type="button"
            variant="outline"
            onClick={toolbar.onImport}
            aria-label="Importar"
            title="Importar"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg sm:h-10 sm:w-10"
          >
            <ImportIcon className="size-4" />
          </Button>
        ) : null}
        {toolbar.onCreate && canMutate ? (
          <Button
            type="button"
            data-tour="module-create"
            onClick={toolbar.onCreate}
            className="min-h-11 flex-1 rounded-lg px-4 py-2 text-sm font-medium sm:flex-none sm:min-h-0"
          >
            {toolbar.createLabel ?? "+ Nuevo registro"}
          </Button>
        ) : null}
      </div>
    </Card>
  )
}

/** A breadcrumb segment: a plain label (current page, not clickable) or a label + route to navigate back to. */
export type BreadcrumbItem = string | { label: string; to: string }

type ModuleLayoutProps = {
  breadcrumbs: BreadcrumbItem[]
  title: string
  subtitle: string
  statusLabel?: string
  /**
   * Botones de acción en el encabezado. Reservado para páginas de detalle/formulario
   * (ej. "Guardar", "Publicar"). Las páginas de gestor (listado) NO deben usar esta
   * prop — sus acciones van en `toolbar` (onImport/onCreate), el encabezado del
   * gestor es solo informativo.
   */
  actions?: ReactNode
  tabs: ModuleTab[]
  metrics: ModuleMetric[]
  toolbar?: ModuleToolbar
  /**
   * En pantallas pequeñas oculta migas y subtítulo y reduce el título, para pantallas de campo
   * (ej. check-in) donde la primera vista debe ser la tarea y no el encabezado. En `sm` y más
   * grande el encabezado es el de siempre.
   */
  compactOnMobile?: boolean
  children: ReactNode
}

function ModuleFilterControl({ filter }: { filter: ModuleFilter }) {
  if (filter.type === "checkbox") {
    return (
      <Button
        type="button"
        variant={filter.checked ? "default" : "outline"}
        onClick={() => filter.onChange(!filter.checked)}
        aria-pressed={filter.checked}
        className="rounded-lg px-3 py-2 text-sm font-medium"
      >
        {filter.label}
      </Button>
    )
  }

  if (filter.type === "text") {
    return (
      <input
        type="text"
        value={filter.value}
        onChange={(e) => filter.onChange(e.target.value)}
        placeholder={filter.placeholder}
        aria-label={filter.label}
        className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm dark:border-gray-700 dark:bg-gray-900"
      />
    )
  }

  return (
    <select
      value={filter.value}
      onChange={(e) => filter.onChange(e.target.value)}
      className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm dark:border-gray-700 dark:bg-gray-900"
      aria-label={filter.label}
    >
      {filter.options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  )
}

const METRIC_TONE_STYLES: Record<
  ModuleMetricTone,
  { value: string; dot: string; iconBox: string; card: string }
> = {
  neutral: {
    value: "text-gray-800 dark:text-white/90",
    dot: "bg-gray-300 dark:bg-gray-600",
    iconBox: "bg-brand-50 text-brand-600 dark:bg-brand-500/[0.12] dark:text-brand-400",
    card: "border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]",
  },
  good: {
    value: "text-success-600 dark:text-success-500",
    dot: "bg-success-500",
    iconBox: "bg-success-50 text-success-600 dark:bg-success-500/[0.12] dark:text-success-400",
    card: "border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]",
  },
  warning: {
    value: "text-warning-600 dark:text-orange-400",
    dot: "bg-warning-500",
    iconBox: "bg-warning-100 text-warning-600 dark:bg-warning-500/[0.15] dark:text-warning-400",
    card: "border-warning-200 bg-warning-25 dark:border-warning-500/30 dark:bg-warning-500/[0.06]",
  },
  danger: {
    value: "text-error-600 dark:text-error-500",
    dot: "bg-error-500",
    iconBox: "bg-error-50 text-error-600 dark:bg-error-500/[0.12] dark:text-error-400",
    card: "border-error-200 bg-error-25 dark:border-error-500/30 dark:bg-error-500/[0.06]",
  },
}

function ModuleMetricCard({ metric }: { metric: ModuleMetric }) {
  const tone = METRIC_TONE_STYLES[metric.tone ?? "neutral"]

  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5">
          {metric.tone && metric.tone !== "neutral" && !metric.icon ? (
            <span className={`size-1.5 shrink-0 rounded-full ${tone.dot}`} aria-hidden="true" />
          ) : null}
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{metric.label}</p>
        </div>
        {metric.icon ? (
          <div className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${tone.iconBox}`}>
            {metric.icon}
          </div>
        ) : null}
      </div>
      <p className={`mt-1 text-2xl font-semibold ${tone.value}`}>{metric.value}</p>
      {metric.trend ? <p className="text-xs text-gray-500 dark:text-gray-400">{metric.trend}</p> : null}
      {metric.hint ? (
        <p className="mt-1 text-xs leading-snug text-gray-500 dark:text-gray-500">{metric.hint}</p>
      ) : null}
    </>
  )

  const className =
    `rounded-xl p-3 ${tone.card}` +
    (metric.to ? " transition-colors hover:border-brand-300 dark:hover:border-brand-700" : "")

  if (metric.to) {
    return (
      <Link to={metric.to} className="block">
        <Card className={className}>{body}</Card>
      </Link>
    )
  }

  return <Card className={className}>{body}</Card>
}

export default function ModuleLayout({
  breadcrumbs,
  title,
  subtitle,
  statusLabel,
  actions,
  tabs,
  metrics,
  toolbar,
  compactOnMobile = false,
  children,
}: ModuleLayoutProps) {
  return (
    <section className="space-y-5">
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className={`${compactOnMobile ? "hidden sm:flex" : "flex"} flex-wrap items-center gap-1 text-xs text-gray-500 dark:text-gray-400`}>
              {breadcrumbs.map((crumb, i) => {
                const label = typeof crumb === "string" ? crumb : crumb.label
                const to = typeof crumb === "string" ? undefined : crumb.to
                return (
                  <span key={`${label}-${i}`} className="flex items-center gap-1">
                    {i > 0 ? <span>&gt;</span> : null}
                    {to ? (
                      <Link to={to} className="hover:text-primary hover:underline">
                        {label}
                      </Link>
                    ) : (
                      <span>{label}</span>
                    )}
                  </span>
                )
              })}
            </p>
            <div className={`${compactOnMobile ? "mt-0 sm:mt-1" : "mt-1"} flex items-center gap-2`}>
              <h1 className={`${compactOnMobile ? "text-xl sm:text-2xl" : "text-2xl"} font-semibold text-gray-800 dark:text-white/90`}>{title}</h1>
              {statusLabel ? (
                <span className="rounded-full bg-success-100 px-2 py-0.5 text-xs font-medium text-success-700">
                  {statusLabel}
                </span>
              ) : null}
            </div>
            <p className={`${compactOnMobile ? "hidden sm:block" : ""} mt-1 text-sm text-gray-500 dark:text-gray-400`}>{subtitle}</p>
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </div>

        {tabs.length > 0 ? (
          <nav className="mt-4 flex flex-wrap gap-2" aria-label="Secciones">
            {tabs.map((tab) => (
              <Link
                key={tab.to}
                to={tab.to}
                aria-current={tab.active ? "page" : undefined}
                className={`inline-flex min-h-11 items-center rounded-lg px-3 py-1.5 text-sm font-medium sm:min-h-0 ${
                  tab.active
                    ? "bg-primary/10 text-primary"
                    : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/[0.04]"
                }`}
              >
                {tab.label}
              </Link>
            ))}
          </nav>
        ) : null}
      </div>

      {metrics.length > 0 ? (
        <div data-tour="module-metrics" className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
          {metrics.map((metric) => (
            <ModuleMetricCard key={metric.label} metric={metric} />
          ))}
        </div>
      ) : null}

      {toolbar ? <ModuleToolbar toolbar={toolbar} /> : null}

      <div data-tour="module-table">{children}</div>
    </section>
  )
}
